import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, Calculator, CircleAlert, CircleCheck, Cpu, FileQuestion, Gauge, Hourglass, Info, KeyRound, Lock,
  MessageCircle, NotebookPen, PauseCircle, RotateCcw, Save, ShieldCheck, Sparkles, Target, Users, Wand2, X,
} from 'lucide-react';
import { AppShell, Button, Loader, PageHeader, ScoreCard } from '../components/ui';
import { aiService, apiErrorMessage } from '../services/api';
import {
  MODELS, PROVIDERS, ago, estimate, fmtBRL, fmtCompact, fmtInt, fmtUSD, maskedKey, modelLabel, providerLabel,
} from '../utils/ai';
import './AdminAI.css';

const TASK_ICON = {
  TUTOR: MessageCircle,
  ACTIVE_REVIEW: Target,
  QUESTIONS: FileQuestion,
  ERROR_ANALYSIS: NotebookPen,
  ARTICLE_REVIEW: ShieldCheck,
  CONTENT_GENERATION: Wand2,
};

const EDITABLE = [
  'provider', 'modelDefault', 'modelsByTask', 'ollamaBaseUrl', 'messagesPerDay', 'reviewsPerDay', 'monthlyCap',
  'onUnavailable', 'userKeysAllowed', 'providersAllowed', 'reviewRules', 'forbiddenTerms', 'priceInput', 'priceOutput',
  'exchangeRate',
];

const pick = (s) => Object.fromEntries(EDITABLE.map(k => [k, s[k]]));

/** Inteligência artificial (/admin/ia): provedor, chave, modelo por tarefa, revisão, limites e calculadora. */
function AdminAI() {
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([aiService.settings(), aiService.usage().catch(() => null)]);
      setSaved(s);
      setForm(pick(s));
      setUsage(u);
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para carregar a configuração.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!saved || !form) return false;
    return newKey !== null || JSON.stringify(pick(saved)) !== JSON.stringify(form);
  }, [saved, form, newKey]);

  if (!form) {
    return (
      <AppShell section="Inteligência artificial">
        {error ? <p className="aia-error" role="alert"><CircleAlert size={16} aria-hidden="true" />{error}</p> : <Loader />}
      </AppShell>
    );
  }

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { ...form };
      if (newKey !== null && newKey.trim()) body.apiKey = newKey.trim();
      const s = await aiService.saveSettings(body);
      setSaved(s);
      setForm(pick(s));
      setNewKey(null);
      setNotice('Alterações salvas.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setForm(pick(saved));
    setNewKey(null);
    setError(null);
  };

  const test = async () => {
    setTesting(true);
    setNotice(null);
    try {
      const r = await aiService.testConnection();
      setSaved(r.settings);
      if (!dirty) setForm(pick(r.settings));
      setNotice(r.ok ? `Conexão ok · resposta em ${(r.latencyMs / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : null);
      if (!r.ok) setError(r.error || 'A conexão falhou.');
    } catch (err) {
      setError(apiErrorMessage(err, 'Não deu para testar.'));
    } finally {
      setTesting(false);
    }
  };

  const month = usage?.month || {};

  return (
    <AppShell section="Inteligência artificial">
      <PageHeader
        eyebrow="Administração"
        title="Inteligência artificial"
        subtitle="Chave, modelo e limites de tudo que usa IA no StudySpace."
        actions={(
          <>
            <Button variant="ghost" onClick={discard} disabled={!dirty || saving}>Descartar</Button>
            <Button icon={Save} onClick={save} disabled={!dirty || saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</Button>
          </>
        )}
      />

      {error && <p className="aia-error" role="alert"><CircleAlert size={16} aria-hidden="true" />{error}</p>}
      {notice && !error && <p className="aia-notice" role="status"><CircleCheck size={16} aria-hidden="true" />{notice}</p>}

      <div className="aia">
        <StatusCard saved={saved} usage={usage} month={month} testing={testing} onTest={test} dirty={dirty} />

        <div className="aia-row">
          <div className="aia-col">
            <ProviderCard form={form} saved={saved} set={set} newKey={newKey} setNewKey={setNewKey} />
            <UserKeysCard form={form} set={set} />
          </div>
          <ModelsCard form={form} saved={saved} set={set} />
        </div>

        <div className="aia-row">
          <ReviewCard form={form} saved={saved} set={set} />
          <LimitsCard form={form} set={set} />
        </div>

        <CalculatorCard form={form} set={set} usage={usage} />
      </div>
    </AppShell>
  );
}

// ── Status ──────────────────────────────────────────────────────────

function StatusCard({ saved, usage, month, testing, onTest, dirty }) {
  const ok = saved.configured && saved.lastTestOk !== false;
  const state = !saved.configured ? 'off' : saved.lastTestOk === false ? 'error' : 'on';
  const label = { on: 'Conectado', error: 'Falhou no último teste', off: 'Sem chave' }[state];
  const lastTest = saved.lastTestAt
    ? `Último teste ${ago(saved.lastTestAt)}${saved.lastTestLatencyMs != null ? ` · resposta em ${(saved.lastTestLatencyMs / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s` : ''}`
    : saved.configured ? 'Ainda não testado' : `Salve uma chave ou defina ${saved.envVariable} no servidor`;
  return (
    <ScoreCard
      className="aia-status"
      inline
      stripe
      top={(
        <div className="aia-status__top">
          <span className={`aia-status__icon ${ok ? '' : 'is-off'}`}><Sparkles size={26} aria-hidden="true" /></span>
          <div className="aia-status__text">
            <span className={`aia-status__state aia-status__state--${state}`}><span aria-hidden="true" />{label}</span>
            <strong>{providerLabel(saved.provider)} · {modelLabel(saved.modelDefault)}</strong>
            <span>{lastTest}{saved.lastTestOk === false && saved.lastTestError ? ` · ${saved.lastTestError}` : ''}</span>
          </div>
          <Button variant="outline" className="aia-status__test" icon={Activity} onClick={onTest} disabled={testing || dirty}
                  title={dirty ? 'Salve antes de testar' : undefined}>
            {testing ? 'Testando...' : 'Testar conexão'}
          </Button>
        </div>
      )}
      items={[
        { icon: MessageCircle, value: fmtInt(month.calls), label: 'chamadas', hint: 'no mês' },
        { icon: Users, value: fmtInt(month.people), label: 'pessoas usaram' },
        { icon: ShieldCheck, value: fmtInt(month.reviews), label: 'revisões de artigo', hint: `· ${fmtInt(usage?.blockedReviewsThisMonth)} bloqueadas` },
        { icon: Hourglass, value: fmtInt(usage?.limitReachedToday), label: 'chegaram no limite', hint: 'hoje' },
      ]}
    />
  );
}

// ── Provedor e chave ────────────────────────────────────────────────

function ProviderCard({ form, saved, set, newKey, setNewKey }) {
  const providerChanged = form.provider !== saved.provider;
  const envName = form.provider === 'anthropic' ? 'ANTHROPIC_API_KEY' : 'OPENAI_API_KEY';
  const changeProvider = (p) => {
    const first = MODELS[p]?.[0]?.id;
    set({ provider: p, modelDefault: p === saved.provider ? saved.modelDefault : (first || form.modelDefault), modelsByTask: {} });
  };
  return (
    <section className="aia-card">
      <h2 className="aia-card__title"><KeyRound size={15} aria-hidden="true" />Provedor e chave</h2>
      <div className="aia-options aia-options--3" role="radiogroup" aria-label="Provedor">
        {PROVIDERS.map(p => (
          <button key={p.value} type="button" role="radio" aria-checked={form.provider === p.value}
                  className={`aia-option ${form.provider === p.value ? 'is-active' : ''}`} onClick={() => changeProvider(p.value)}>
            <strong>{p.label}</strong>
            <span>{p.hint}</span>
          </button>
        ))}
      </div>

      {form.provider === 'ollama' ? (
        <label className="aia-field">
          <span className="aia-label">Endereço do Ollama</span>
          <input className="ss-input aia-mono" value={form.ollamaBaseUrl || ''} placeholder="http://localhost:11434"
                 onChange={(e) => set({ ollamaBaseUrl: e.target.value })} />
          <span className="aia-hint">O servidor do StudySpace precisa alcançar este endereço. Sem chave.</span>
        </label>
      ) : (
        <div className="aia-field">
          <span className="aia-label">Chave da API</span>
          {newKey === null && !providerChanged && saved.keyLast4 ? (
            <div className="aia-keyrow">
              <span className="aia-key"><Lock size={14} aria-hidden="true" />{maskedKey(saved.provider, saved.keyLast4)}</span>
              <Button variant="outline" onClick={() => setNewKey('')}>Trocar</Button>
            </div>
          ) : (
            <div className="aia-keyrow">
              <input className="ss-input aia-mono" type="password" autoComplete="off" value={newKey || ''}
                     placeholder={form.provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
                     onChange={(e) => setNewKey(e.target.value)} aria-label="Nova chave da API" />
              {saved.keyLast4 && !providerChanged && (
                <Button variant="ghost" icon={X} onClick={() => setNewKey(null)}>Cancelar</Button>
              )}
            </div>
          )}
          <span className="aia-hint">
            Guardada criptografada no servidor; ninguém vê a chave inteira depois de salvar.
            {saved.keySource === 'saved' && saved.updatedAt ? ` Atualizada ${ago(saved.updatedAt)}.` : ''}
            {!saved.canStoreKeys && ' Defina AI_SECRET no servidor para poder salvar.'}
          </span>
          <p className="aia-note">
            <Info size={14} aria-hidden="true" />
            <span>
              {saved.keySource === 'env' && !providerChanged
                ? <>Usando <code>{envName}</code> do ambiente. Ela vale até uma chave ser salva aqui.</>
                : <>Se houver <code>{envName}</code> no ambiente, ela vale até uma chave ser salva aqui.</>}
            </span>
          </p>
        </div>
      )}
    </section>
  );
}

function UserKeysCard({ form, set }) {
  const toggleProvider = (p) => {
    const list = form.providersAllowed.includes(p) ? form.providersAllowed.filter(x => x !== p) : [...form.providersAllowed, p];
    set({ providersAllowed: list });
  };
  return (
    <section className="aia-card">
      <h2 className="aia-card__title"><Users size={15} aria-hidden="true" />Chave própria dos usuários</h2>
      <div className="ss-seg aia-seg" role="radiogroup" aria-label="Chave própria">
        {[[false, 'Não permitir'], [true, 'Permitir como reserva']].map(([v, label]) => (
          <button key={String(v)} type="button" role="radio" aria-checked={form.userKeysAllowed === v}
                  className={`ss-seg__item ${form.userKeysAllowed === v ? 'is-active' : ''}`} onClick={() => set({ userKeysAllowed: v })}>
            {label}
          </button>
        ))}
      </div>
      <p className="aia-text">
        Se a chave do sistema não estiver configurada, falhar ou chegar no limite, quem cadastrou a própria chave em
        Minha conta continua usando a IA. Os outros veem o aviso de indisponível.
      </p>
      <div className="aia-chips" role="group" aria-label="Provedores aceitos">
        <span className="aia-label">Provedores aceitos</span>
        {PROVIDERS.map(p => {
          const on = form.providersAllowed.includes(p.value);
          return (
            <button key={p.value} type="button" aria-pressed={on} disabled={!form.userKeysAllowed}
                    className={`aia-chip ${on ? 'is-active' : ''}`} onClick={() => toggleProvider(p.value)}>
              {on && '✓ '}{p.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── Modelo por tarefa ───────────────────────────────────────────────

function ModelSelect({ provider, value, onChange, allowDefault, className = '', ariaLabel }) {
  const list = MODELS[provider] || [];
  const known = list.some(m => m.id === value);
  const [custom, setCustom] = useState(!known && !!value);
  if (!list.length || custom) {
    return (
      <span className={`aia-modelinput ${className}`}>
        <input className="ss-input aia-mono" value={value || ''} placeholder={allowDefault ? 'Usar o padrão' : 'nome do modelo'}
               aria-label={ariaLabel} onChange={(e) => onChange(e.target.value)} />
        {list.length > 0 && <button type="button" className="aia-linkbtn" onClick={() => { setCustom(false); onChange(allowDefault ? '' : list[0].id); }}>lista</button>}
      </span>
    );
  }
  return (
    <select className={`ss-input aia-select ${className}`} value={value || ''} aria-label={ariaLabel}
            onChange={(e) => (e.target.value === '__custom' ? setCustom(true) : onChange(e.target.value))}>
      {allowDefault && <option value="">Usar o padrão</option>}
      {list.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
      <option value="__custom">Outro modelo...</option>
    </select>
  );
}

function ModelsCard({ form, saved, set }) {
  const overrides = Object.values(form.modelsByTask || {}).filter(Boolean).length;
  return (
    <section className="aia-card aia-card--fill">
      <h2 className="aia-card__title">
        <Cpu size={15} aria-hidden="true" />Modelo por tarefa
        <span className="aia-card__aside">{overrides ? `${overrides} com modelo próprio` : 'Todas usam o padrão'}</span>
      </h2>
      <label className="aia-default">
        <span>Padrão</span>
        <ModelSelect key={form.provider} provider={form.provider} value={form.modelDefault} ariaLabel="Modelo padrão"
                     onChange={(v) => set({ modelDefault: v })} />
      </label>
      <ul className="aia-tasks">
        {saved.tasks.map(t => {
          const Icon = TASK_ICON[t.key] || Sparkles;
          return (
            <li key={t.key}>
              <span className="aia-tasks__icon"><Icon size={14} aria-hidden="true" /></span>
              <span className="aia-tasks__name">{t.label}</span>
              <ModelSelect key={form.provider} provider={form.provider} allowDefault value={form.modelsByTask?.[t.key] || ''}
                           ariaLabel={`Modelo para ${t.label}`}
                           onChange={(v) => set({ modelsByTask: { ...form.modelsByTask, [t.key]: v } })} />
            </li>
          );
        })}
      </ul>
      <p className="aia-note aia-note--foot">
        <KeyRound size={14} aria-hidden="true" />
        <span>Quem usa chave própria escolhe o modelo dela em Minha conta. Os limites daqui valem só para a chave do sistema.</span>
      </p>
    </section>
  );
}

// ── Revisão de artigos ──────────────────────────────────────────────

function ReviewCard({ form, saved, set }) {
  const [term, setTerm] = useState('');
  const addTerm = () => {
    const t = term.trim().replace(/,$/, '');
    if (t && !form.forbiddenTerms.includes(t)) set({ forbiddenTerms: [...form.forbiddenTerms, t] });
    setTerm('');
  };
  return (
    <section className="aia-card">
      <h2 className="aia-card__title"><ShieldCheck size={15} aria-hidden="true" />Revisão de artigos públicos</h2>
      <ul className="aia-rules">
        {saved.rules.map(r => {
          const on = form.reviewRules[r.key] !== false;
          return (
            <li key={r.key}>
              <button type="button" role="switch" aria-checked={on} className={`aia-switch ${on ? 'is-on' : ''}`}
                      onClick={() => set({ reviewRules: { ...form.reviewRules, [r.key]: !on } })} aria-label={r.label}>
                <span />
              </button>
              <span className="aia-rules__label">{r.label}</span>
              <span className={`aia-sev aia-sev--${r.severity === 'GRAVE' ? 'grave' : 'atencao'}`}>
                {r.severity === 'GRAVE' ? 'Bloqueia' : 'Atenção'}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="aia-field aia-field--sep">
        <span className="aia-label">Clientes e termos que nunca podem aparecer <span className="aia-muted">· a IA também procura variações</span></span>
        <div className="aia-terms" onClick={(e) => e.currentTarget.querySelector('input')?.focus()}>
          {form.forbiddenTerms.map(t => (
            <span key={t} className="aia-term">
              {t}
              <button type="button" aria-label={`Remover ${t}`} onClick={() => set({ forbiddenTerms: form.forbiddenTerms.filter(x => x !== t) })}>
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ))}
          <input value={term} placeholder="Adicionar..." aria-label="Adicionar termo"
                 onChange={(e) => setTerm(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTerm(); }
                   if (e.key === 'Backspace' && !term && form.forbiddenTerms.length) set({ forbiddenTerms: form.forbiddenTerms.slice(0, -1) });
                 }}
                 onBlur={addTerm} />
        </div>
        <span className="aia-hint">Lista visível só para super admin; não vai no texto mostrado ao autor. Use * como curinga (ex.: *.corp).</span>
      </div>
    </section>
  );
}

// ── Limites ─────────────────────────────────────────────────────────

function NumberField({ label, hint, unit, value, onChange }) {
  return (
    <label className="aia-limit">
      <span className="aia-limit__text"><strong>{label}</strong><span>{hint}</span></span>
      <span className="aia-limit__input">
        <input type="number" min="0" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
        <span>{unit}</span>
      </span>
    </label>
  );
}

function LimitsCard({ form, set }) {
  return (
    <section className="aia-card">
      <h2 className="aia-card__title"><Gauge size={15} aria-hidden="true" />Limites</h2>
      <NumberField label="Mensagens por pessoa" hint="Tutor, revisão ativa e questões somados" unit="por dia"
                   value={form.messagesPerDay} onChange={(v) => set({ messagesPerDay: v })} />
      <NumberField label="Revisões de artigo por pessoa" hint='Inclui o "Revisar com IA" voluntário' unit="por dia"
                   value={form.reviewsPerDay} onChange={(v) => set({ reviewsPerDay: v })} />
      <NumberField label="Teto de chamadas no mês" hint="Ao chegar, a IA da chave do sistema pausa até o mês virar" unit="chamadas"
                   value={form.monthlyCap} onChange={(v) => set({ monthlyCap: v })} />
      <div className="aia-field aia-field--sep">
        <span className="aia-label">Quando a chave do sistema não responder <span className="aia-muted">· sem chave, erro ou limite</span></span>
        <div className="aia-options aia-options--2" role="radiogroup" aria-label="Quando a chave do sistema não responder">
          {[
            ['PAUSE', PauseCircle, 'Pausar a IA', 'Mostra o aviso de indisponível'],
            ['USE_USER_KEY', KeyRound, 'Usar a chave da pessoa', form.userKeysAllowed ? 'Para quem cadastrou uma' : 'Precisa permitir chave própria'],
          ].map(([v, Icon, label, hint]) => (
            <button key={v} type="button" role="radio" aria-checked={form.onUnavailable === v}
                    className={`aia-option ${form.onUnavailable === v ? 'is-active' : ''}`} onClick={() => set({ onUnavailable: v })}>
              <strong><Icon size={14} aria-hidden="true" />{label}</strong>
              <span>{hint}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Calculadora ─────────────────────────────────────────────────────

const SLIDERS = [
  { key: 'people', label: 'Pessoas usando IA', min: 1, max: 300, unit: 'pessoas', realKey: 'people', realLabel: (v) => `último mês: ${v}` },
  { key: 'msgs', label: 'Mensagens por pessoa', min: 1, max: 40, unit: '/dia', limitHint: true },
  { key: 'days', label: 'Dias de estudo no mês', min: 1, max: 30, unit: 'dias' },
  { key: 'reviews', label: 'Revisões de artigo', min: 0, max: 300, unit: '/mês', hint: 'obrigatórias + voluntárias' },
  { key: 'tin', label: 'Tokens enviados por chamada', min: 500, max: 20000, step: 100, unit: 'tok', hint: 'aula + histórico', compact: true },
  { key: 'tout', label: 'Tokens na resposta', min: 100, max: 4000, step: 50, unit: 'tok', compact: true },
];

function fromUsage(u) {
  const m = u && u.calls > 0 ? u : null;
  return {
    people: Math.max(1, m?.people || 30),
    msgs: Math.max(1, m?.messagesPerPersonPerDay || 10),
    days: Math.max(1, m?.studyDays || 20),
    reviews: m?.reviews || 20,
    tin: Math.max(500, m?.avgTokensIn || 4000),
    tout: Math.max(100, m?.avgTokensOut || 600),
  };
}

function CalculatorCard({ form, set, usage }) {
  const real = usage?.lastMonth?.calls > 0 ? usage.lastMonth : usage?.month;
  const [vals, setVals] = useState(() => fromUsage(real));
  const r = estimate({
    ...vals, pin: Number(form.priceInput) || 0, pout: Number(form.priceOutput) || 0, fx: Number(form.exchangeRate) || 0,
    cap: Number(form.monthlyCap) || 0, limit: Number(form.messagesPerDay) || 0,
  });
  const tone = r.capPct > 100 ? 'over' : r.capPct >= 80 ? 'warn' : 'ok';
  const knownPrice = MODELS[form.provider]?.find(m => m.id === form.modelDefault);
  const usePrice = () => knownPrice && set({ priceInput: knownPrice.input, priceOutput: knownPrice.output });

  return (
    <section className="aia-calc">
      <div className="aia-calc__inputs">
        <h2 className="aia-card__title">
          <Calculator size={15} aria-hidden="true" />Calculadora de uso e custo
          <button type="button" className="aia-card__action" onClick={() => setVals(fromUsage(real))}>
            <RotateCcw size={13} aria-hidden="true" />Usar dados do último mês
          </button>
        </h2>
        {SLIDERS.map(s => {
          const v = vals[s.key];
          const max = s.key === 'msgs' ? Math.max(s.max, Number(form.messagesPerDay) || s.max) : s.max;
          const aside = s.realKey && real?.[s.realKey] != null ? s.realLabel(real[s.realKey])
            : s.limitHint ? `limite: ${form.messagesPerDay}/dia` : s.hint;
          return (
            <label key={s.key} className="aia-slider">
              <span className="aia-slider__head"><strong>{s.label}</strong>{aside && <span>{aside}</span>}</span>
              <input type="range" min={s.min} max={max} step={s.step || 1} value={v}
                     onChange={(e) => setVals(x => ({ ...x, [s.key]: Number(e.target.value) }))}
                     style={{ '--pct': `${((v - s.min) / (max - s.min)) * 100}%` }} />
              <span className="aia-slider__value"><b>{s.compact ? fmtCompact(v) : fmtInt(v)}</b> {s.unit}</span>
            </label>
          );
        })}
        <div className="aia-prices">
          <span className="aia-prices__head">
            <strong>Preço do modelo ({modelLabel(form.modelDefault)})</strong>
            {knownPrice && (
              <button type="button" className="aia-linkbtn" onClick={usePrice}>usar preço de tabela</button>
            )}
          </span>
          <div className="aia-prices__grid">
            {[
              ['priceInput', 'Entrada / 1M tokens', 'US$'],
              ['priceOutput', 'Saída / 1M tokens', 'US$'],
              ['exchangeRate', 'Câmbio', 'R$'],
            ].map(([k, label, cur]) => (
              <label key={k} className="aia-price">
                <span>{label}</span>
                <span className="aia-price__input">
                  <span>{cur}</span>
                  <input type="number" min="0" step="0.01" value={form[k] ?? ''}
                         onChange={(e) => set({ [k]: e.target.value === '' ? 0 : Number(e.target.value) })} />
                </span>
              </label>
            ))}
          </div>
          <span className="aia-hint">Valores de exemplo. Preencha com o preço do seu contrato; ficam salvos com as alterações.</span>
        </div>
      </div>

      <div className="aia-calc__result">
        <span className="aia-calc__eyebrow">Estimativa do mês</span>
        <strong className="aia-calc__brl">{fmtBRL(r.brlMonth)}</strong>
        <span className="aia-calc__sub">{fmtUSD(r.usdMonth)} · {fmtBRL(r.perPerson)} por pessoa</span>
        <div className="aia-calc__grid">
          <span><b>{fmtInt(r.calls)}</b>chamadas no mês</span>
          <span><b>{fmtInt(r.perDay)}</b>chamadas por dia</span>
          <span><b>{fmtCompact(r.tokens)}</b>tokens no mês</span>
          <span><b>{fmtBRL(r.brlPerDay)}</b>por dia de estudo</span>
        </div>
        <div className={`aia-cap aia-cap--${tone}`}>
          <span className="aia-cap__head">
            <span>Teto do mês · {fmtInt(form.monthlyCap)} chamadas</span>
            <b>{Math.round(r.capPct)}%</b>
          </span>
          <span className="aia-cap__bar"><span style={{ width: `${Math.min(100, r.capPct)}%` }} /></span>
          {tone !== 'ok' && (
            <p className="aia-cap__msg">
              <CircleAlert size={15} aria-hidden="true" />
              <span>
                {tone === 'over'
                  ? <>Passa do teto por volta do dia {r.overDay}. Para caber, limite em {r.suggested} {r.suggested === 1 ? 'mensagem' : 'mensagens'} por pessoa/dia ou suba o teto.</>
                  : <>Chega perto do teto. Com este uso, cabem até {r.suggested} mensagens por pessoa/dia.</>}
              </span>
            </p>
          )}
        </div>
        <div className="aia-calc__worst">
          <span><small>Pior caso</small>todos no limite de {form.messagesPerDay}/dia</span>
          <b>{fmtBRL(r.worstBrl)}</b>
        </div>
      </div>
    </section>
  );
}

export default AdminAI;
